<script setup>
  import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
  import { useRoute } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { useTheme } from 'vuetify'
  import Loader from '@/components/Loader.vue'
  import DefaultLayout from '@/layouts/DefaultLayout.vue'
  import EmptyLayout from '@/layouts/EmptyLayout.vue'
  import DefaultNewLayout from '@/layouts/DefaultNewLayout.vue'
  import MainLayouts from '@/layouts/MainLayouts.vue'
  import adBlockDetector from '@/services/utils/ad-block-detector'
  import { useSettingsStore } from '@/store/settings/settings'

  const route = useRoute()
  const vuetifyTheme = useTheme()
  const settingsStore = useSettingsStore()
  const { adNetwork } = storeToRefs(settingsStore)
  const { setVuetifyColor } = settingsStore
  const { proxy } = getCurrentInstance()

  const isAdBlockDetected = ref(false)

  const title = computed(() => {
    const routeTitle = route.meta.title ? ` -  ${route.meta.title}` : ''
    return adNetwork.value.name + routeTitle
  })
  const description = computed(() => {
    const routeDescription = route.meta.description ? ` - ${route.meta.description}` : ''
    return adNetwork.value.name + routeDescription
  })
  const layoutComponent = computed(() => {
    return (
      {
        DefaultLayout: DefaultLayout,
        EmptyLayout: EmptyLayout,
        DefaultNewLayout: DefaultNewLayout,
        MainLayouts: MainLayouts
      }[route.meta.layoutName] ?? Loader
    )
  })

  watch(
    title,
    (newTitle) => {
      document.title = newTitle
    },
    { immediate: true }
  )
  watch(
    description,
    (newDescription) => {
      const metaTag = document.querySelector('meta[name="description"]')
      metaTag.setAttribute('content', newDescription)
    },
    { immediate: true }
  )
  watch(adNetwork, () => {
    setVuetifyColor(vuetifyTheme)
  })

  const detectAdBlock = async () => {
    const result = await adBlockDetector()
    if (result) {
      isAdBlockDetected.value = result.blocked / result.total >= 0.5
    } else {
      isAdBlockDetected.value = true
    }
  }

  detectAdBlock()

  onMounted(() => {
    const { user } = proxy.$auth
    proxy.$pixel.init(
      user?.id
        ? {
            user_id: user.id,
            is_impersonating: !!user.is_impersonating,
            ...(!!user.is_impersonating && { impersonator_id: user.impersonator?.id })
          }
        : null
    )
  })
</script>

<template>
  <component :is="layoutComponent" :is-ad-block-detected="isAdBlockDetected" />
</template>
